import * as THREE from 'three';
import { RIVER_WIDTH, RIVER_Z, riverCenterZ } from './river';

export const CHUNK_SIZE = 32;
const RING = 2; // 5×5 ground tiles
const RIVER_RING = 2; // river segments along X around camera
const RIVER_SEGS_X = 60;
const RIVER_SEGS_W = 10;

export interface TerrainChunksHandles {
  updateVisualWorld: (target: THREE.Vector3) => void;
}

function createRiverSegmentGeometry(chunkX: number): THREE.PlaneGeometry {
  const geo = new THREE.PlaneGeometry(CHUNK_SIZE, RIVER_WIDTH, RIVER_SEGS_X, RIVER_SEGS_W);
  const pos = geo.attributes.position;
  const half = CHUNK_SIZE * 0.5;
  const worldCenterX = chunkX * CHUNK_SIZE + half;

  for (let i = 0; i < pos.count; i++) {
    const localX = pos.getX(i);
    const localY = pos.getY(i);
    const worldX = worldCenterX + localX;
    // After rotation.x = -π/2: worldZ = position.z - localY. Subtract curve so centerline matches riverCenterZ.
    const curveOffset = riverCenterZ(worldX) - RIVER_Z;
    pos.setY(i, localY - curveOffset);
    pos.setZ(i, 0.03 * Math.sin(worldX * 4.0));
  }
  geo.computeVertexNormals();
  return geo;
}

export function createTerrainChunks(scene: THREE.Scene): TerrainChunksHandles {
  const groundMat = new THREE.MeshStandardMaterial({
    color: 0x3d5c32,
    roughness: 0.95,
    metalness: 0.05,
  });
  const groundGeo = new THREE.PlaneGeometry(CHUNK_SIZE, CHUNK_SIZE);
  const groundMeshes: THREE.Mesh[] = [];

  for (let i = 0; i < (RING * 2 + 1) ** 2; i++) {
    const mesh = new THREE.Mesh(groundGeo, groundMat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.y = 0;
    mesh.receiveShadow = true;
    scene.add(mesh);
    groundMeshes.push(mesh);
  }

  const riverMat = new THREE.MeshStandardMaterial({
    color: 0x0ea5e9,
    roughness: 0.1,
    transparent: true,
    opacity: 0.8,
    metalness: 0.8,
    flatShading: true,
  });

  const riverCount = RIVER_RING * 2 + 1;
  const riverMeshes: THREE.Mesh[] = [];
  for (let i = 0; i < riverCount; i++) {
    const mesh = new THREE.Mesh(createRiverSegmentGeometry(0), riverMat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.y = 0.06;
    mesh.receiveShadow = true;
    scene.add(mesh);
    riverMeshes.push(mesh);
  }

  let lastCx = Number.NaN;
  let lastCz = Number.NaN;

  function placeRiverSegment(mesh: THREE.Mesh, chunkX: number): void {
    mesh.geometry.dispose();
    mesh.geometry = createRiverSegmentGeometry(chunkX);
    const half = CHUNK_SIZE * 0.5;
    mesh.position.set(chunkX * CHUNK_SIZE + half, 0.06, RIVER_Z);
  }

  function updateVisualWorld(target: THREE.Vector3): void {
    const cx = Math.floor(target.x / CHUNK_SIZE);
    const cz = Math.floor(target.z / CHUNK_SIZE);
    if (cx === lastCx && cz === lastCz) return;

    const half = CHUNK_SIZE * 0.5;
    let gi = 0;
    for (let dz = -RING; dz <= RING; dz++) {
      for (let dx = -RING; dx <= RING; dx++) {
        const mesh = groundMeshes[gi++]!;
        mesh.position.set((cx + dx) * CHUNK_SIZE + half, 0, (cz + dz) * CHUNK_SIZE + half);
      }
    }

    if (cx !== lastCx) {
      let ri = 0;
      for (let dx = -RIVER_RING; dx <= RIVER_RING; dx++) {
        placeRiverSegment(riverMeshes[ri++]!, cx + dx);
      }
    }

    lastCx = cx;
    lastCz = cz;
  }

  // Initial placement around origin
  updateVisualWorld(new THREE.Vector3(0, 0, 0));

  return { updateVisualWorld };
}
